"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Landmark,
  Info,
  AlertTriangle,
  Calculator,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  KURUM_TIPLERI,
  YILI,
  hesaplaKurumlarVergisi,
  formatTL,
  formatOran,
  type KurumlarVergisiSonuc,
  type KurumTipi,
} from "./kurumlar-vergisi-engine";

const howToSchema = generateHowToSchema({
  title: "Kurumlar Vergisi Nasıl Hesaplanır?",
  description:
    "Yıllık kurum kazancınızı girerek kurumlar vergisi ve geçici vergi taksitlerinizi hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Kurum Tipini Seçin",
      text: "Sektörünüze göre kurum tipini belirleyin.",
    },
    { name: "Yıllık Kazancı Girin", text: "Kurum kazancını TL olarak girin." },
    {
      name: "Sonuçları İnceleyin",
      text: "Vergi matrahı, hesaplanan vergi ve çeyreklik taksitleri görüntüleyin.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

const CEYREK_ADLARI = ["1. Çeyrek", "2. Çeyrek", "3. Çeyrek", "4. Çeyrek"];

export default function KurumlarVergisiHesaplayiciClient() {
  const [kurumTipi, setKurumTipi] = useState<KurumTipi>("genel");
  const [yillikKazanc, setYillikKazanc] = useState("");
  const [gecmisYilZarar, setGecmisYilZarar] = useState("");
  const [sonuc, setSonuc] = useState<KurumlarVergisiSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const kazanc = parseFloat(yillikKazanc);
    if (!yillikKazanc || isNaN(kazanc) || kazanc <= 0) {
      setHata("Lütfen geçerli bir yıllık kazanç giriniz.");
      setSonuc(null);
      return;
    }
    const zarar = parseFloat(gecmisYilZarar) || 0;
    setSonuc(
      hesaplaKurumlarVergisi({
        kurumTipi,
        yillikKazanc: kazanc,
        gecmisYilZarar: zarar,
      })
    );
  }, [kurumTipi, yillikKazanc, gecmisYilZarar]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <section className="bg-gradient-to-br from-slate-700 to-slate-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "Kurumlar Vergisi Hesaplayıcı",
                  href: "/araclar/kurumlar-vergisi-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Landmark className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Kurumlar Vergisi Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} Güncel Oranlar
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Sektörünüze göre kurumlar vergisi ve geçici vergi taksitlerinizi
              hesaplayın. Geçmiş yıl zarar mahsubu dahil.
            </p>
          </div>
        </section>

        {/* Vergi Oranları */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              {YILI} Kurumlar Vergisi Oranları
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {KURUM_TIPLERI.map((k) => (
                <div
                  key={k.value}
                  className={`rounded-xl p-4 text-center cursor-pointer transition-all ${
                    kurumTipi === k.value
                      ? "bg-slate-700 text-white ring-2 ring-slate-400"
                      : "bg-white border border-gray-200 hover:border-slate-300"
                  }`}
                  onClick={() => setKurumTipi(k.value)}
                >
                  <span
                    className={`text-2xl font-bold block ${
                      kurumTipi === k.value ? "text-white" : "text-slate-700"
                    }`}
                  >
                    %{(k.oran * 100).toFixed(0)}
                  </span>
                  <span
                    className={`text-xs block mt-1 ${
                      kurumTipi === k.value ? "text-slate-200" : "text-gray-500"
                    }`}
                  >
                    {k.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="kazanc"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Yıllık Kurum Kazancı
                  </label>
                  <div className="relative">
                    <input
                      id="kazanc"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={yillikKazanc}
                      onChange={(e) => setYillikKazanc(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-gray-900"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      ₺
                    </span>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="zarar"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Geçmiş Yıl Zararı (Opsiyonel)
                  </label>
                  <div className="relative">
                    <input
                      id="zarar"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={gecmisYilZarar}
                      onChange={(e) => setGecmisYilZarar(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-gray-900"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      ₺
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Son 5 yıl zararları mahsup edilebilir
                  </p>
                </div>
              </div>

              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-slate-700 text-white py-4 rounded-xl font-semibold hover:bg-slate-800 active:bg-slate-900 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-slate-700 text-white rounded-xl p-4">
                    <span className="text-xs text-slate-300 block">
                      Hesaplanan Vergi
                    </span>
                    <span className="text-xl font-bold mt-1 block tabular-nums">
                      {formatTL(sonuc.hesaplananVergi)}
                    </span>
                    <span className="text-xs text-slate-300">
                      {formatOran(sonuc.vergiOrani)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Vergi Matrahı
                    </span>
                    <span className="text-lg font-bold text-gray-900 mt-1 block tabular-nums">
                      {formatTL(sonuc.vergiMatrahi)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Efektif Oran
                    </span>
                    <span className="text-lg font-bold text-blue-600 mt-1 block">
                      {formatOran(sonuc.efektifOran)}
                    </span>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <span className="text-xs text-emerald-600 block">
                      Vergi Sonrası Kâr
                    </span>
                    <span className="text-lg font-bold text-emerald-700 mt-1 block tabular-nums">
                      {formatTL(sonuc.vergiSonrasiKar)}
                    </span>
                  </div>
                </div>

                {/* Çeyreklik Taksitler */}
                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">
                    Geçici Vergi Taksitleri
                  </h3>
                  <div className="grid grid-cols-4 gap-3">
                    {sonuc.ceyreklikTaksitler.map((taksit, i) => (
                      <div key={i} className="text-center">
                        <span className="text-xs text-gray-500 block">
                          {CEYREK_ADLARI[i]}
                        </span>
                        <span className="font-bold text-sm text-gray-900 tabular-nums">
                          {formatTL(taksit)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Sık Sorulan Sorular
          </h2>
          <div className="space-y-3">
            <SSSSoru
              soru="Geçici vergi nedir?"
              cevap="Geçici vergi, kurumlar vergisinin peşin tahsilatıdır. Her çeyrek sonunda beyan edilir ve yıl sonunda hesaplanan kurumlar vergisinden mahsup edilir."
            />
            <SSSSoru
              soru="Finans sektörüne neden farklı oran uygulanır?"
              cevap="Bankalar, sigorta şirketleri, leasing ve faktoring şirketleri için %30 oran uygulanır. Bu düzenleme 5520 sayılı Kurumlar Vergisi Kanunu'nun 32. maddesinde belirlenmiştir."
            />
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                Bu hesaplama {YILI} yılı genel oranlarına göredir. Teşvik,
                indirim ve istisnalar dahil değildir.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-2">
              Sonuc Yorumu ve Aksiyon Onerisi
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Kurumlar vergisi orani tek basina yeterli degildir; vergi sonrasi
              kar, bordro maliyeti ve nakit akis planini birlikte degerlendirmek
              gerekir.
            </p>
            <div className="flex flex-wrap gap-2 text-sm">
              <Link
                href="/araclar/gelir-vergisi-hesaplayici"
                className="text-indigo-600 hover:underline"
              >
                Gelir vergisi hesaplayici
              </Link>
              <Link
                href="/cozumler/analitik"
                className="text-indigo-600 hover:underline"
              >
                Finansal IK analitigi
              </Link>
              <Link
                href="/yazilim/ucret-ve-yan-hak-yonetimi"
                className="text-indigo-600 hover:underline"
              >
                Ucret yonetim senaryolari
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Kurumlar Vergisi Planlama Notu
            </h2>
            <h3 className="text-base font-semibold text-gray-800 mb-2">
              Efektif oran ve nakit akis birlikte degerlendirilir
            </h3>
            <p className="text-sm text-gray-600">
              Gecici vergi taksitleri, vergi sonrasi kar ve personel maliyeti
              birlikte izlendiginde finansal kararlar daha isabetli olur.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="kurumlar-vergisi-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-slate-700 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              Finansal Yönetimi Güçlendirin
            </h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              IKAI ile bordro, vergi ve tüm İK süreçlerinizi tek platformda
              yönetin.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-slate-700 px-6 py-3 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
